import { Injectable } from '@angular/core';
import { Amplify } from "aws-amplify";
import { signInWithRedirect } from "aws-amplify/auth";
import {
  SOCIAL_SIGN_IN_REFUSAL_CODES,
  SOCIAL_SIGN_IN_REFUSALS,
  SocialSignInRefusal,
  SocialSignInRefusalResult
} from "../models";

/**
 * Carries a refused Google sign-in from the URL the browser lands on to the
 * login screen that answers it.
 *
 * Cognito refuses in its OAuth return path, not in a response body, so the
 * refusal survives nothing of the previous session - it has to be read off the
 * address bar of a cold load, the same way the account-link callback is.
 */
@Injectable({
  providedIn: 'root'
})
export class SocialSignInService {
  private static readonly ERROR_PARAM = 'error';
  private static readonly DESCRIPTION_PARAM = 'error_description';
  /** Cognito rides its own state along; it means nothing to us and is stripped. */
  private static readonly STATE_PARAM = 'state';
  /** The one parameter that says the Google round trip can still succeed. */
  private static readonly CODE_PARAM = 'code';

  /** The prefix Amplify puts on every one of its own auth storage keys. */
  private static readonly AMPLIFY_STORAGE_PREFIX = 'CognitoIdentityServiceProvider';
  /** The three keys Amplify writes when it sends the browser to Google. */
  private static readonly AMPLIFY_INFLIGHT_KEYS = ['inflightOAuth', 'oauthPKCE', 'oauthState'];

  private pendingRefusal: SocialSignInRefusalResult | null = null;

  /**
   * Reads the refusal straight off `window.location` and removes the params, so
   * a reload does not replay it.
   *
   * This runs before the router has navigated, for the same reason
   * `GoogleLinkService.captureCallbackFromUrl` does.
   */
  captureRefusalFromUrl(): void {
    const params = new URLSearchParams(window.location.search);
    const description = params.get(SocialSignInService.DESCRIPTION_PARAM);

    // Anything but an authorization code means no Google round trip can still
    // finish on this load - a refusal, a cancelled consent screen, a press of
    // the back button, or a plain visit to the login screen.
    if (!params.get(SocialSignInService.CODE_PARAM)) {
      this.endAmplifyOAuthFlow();
    }

    if (!params.get(SocialSignInService.ERROR_PARAM) && !description) {
      return;
    }

    this.pendingRefusal = {
      refusal: this.refusalIn(description),
      rawDescription: description ?? undefined
    };

    this.stripRefusalParams(params);
  }

  /**
   * Returns the captured refusal once and forgets it, so coming back to the
   * login screen later does not reopen the modal.
   */
  consumeRefusal(): SocialSignInRefusalResult | null {
    const refusal = this.pendingRefusal;
    this.pendingRefusal = null;
    return refusal;
  }

  /**
   * Finds the refusal code inside a sentence.
   *
   * Used on the URL parameter and on the error the Amplify authenticator
   * reports, because the two carry the same codes in the same shape.
   */
  refusalIn(text: string | null | undefined): SocialSignInRefusal | undefined {
    if (!text) {
      return undefined;
    }

    return SOCIAL_SIGN_IN_REFUSALS.find(refusal => text.includes(SOCIAL_SIGN_IN_REFUSAL_CODES[refusal]));
  }

  /**
   * Starts a Google sign-in.
   *
   * This is the call the "Sign in with Google" button makes. The button is
   * Amplify's own: it hands `FEDERATED_SIGN_IN` to the authenticator state
   * machine, which invokes `signInWithRedirect({ provider: 'Google' })`. Any
   * screen without that button - the end of the contract-email flow, for one -
   * starts the same trip through here.
   *
   * The browser leaves the app when this resolves. It only ever returns to a
   * caller when Amplify refuses to start, which it does when somebody is
   * already signed in.
   */
  signInWithGoogle(): Promise<void> {
    return signInWithRedirect({ provider: 'Google' });
  }

  /**
   * Tells Amplify the Google round trip is over.
   *
   * `signInWithRedirect` marks the flow as in flight in local storage. Every
   * later call that needs a token - `fetchUserAttributes`, and the session
   * check the authenticator does on its own - waits for that mark to go away.
   * The wait is a promise that only Amplify can resolve, and it resolves it in
   * an unawaited side effect of `Amplify.configure`. A caller that reads the
   * mark before that side effect has cleared it waits for ever: the app then
   * stops on its loading screen, with the contract-email modal open underneath.
   *
   * The mark is therefore cleared here, before anything can wait on it, on
   * every load that carries no authorization code. These are the same three
   * keys Amplify clears itself.
   */
  private endAmplifyOAuthFlow(): void {
    const clientId = Amplify.getConfig().Auth?.Cognito?.userPoolClientId;

    if (!clientId) {
      return;
    }

    for (const key of SocialSignInService.AMPLIFY_INFLIGHT_KEYS) {
      localStorage.removeItem(`${SocialSignInService.AMPLIFY_STORAGE_PREFIX}.${clientId}.${key}`);
    }
  }

  private stripRefusalParams(params: URLSearchParams): void {
    params.delete(SocialSignInService.ERROR_PARAM);
    params.delete(SocialSignInService.DESCRIPTION_PARAM);
    params.delete(SocialSignInService.STATE_PARAM);

    const query = params.toString();
    const cleanUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
    window.history.replaceState(window.history.state, '', cleanUrl);
  }
}
